import React from 'react';
import { Cookie, Shield, Info, Check, AlertCircle, ArrowRight } from 'lucide-react';

const CookiesPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-950">
      {/* Hero Section */}
      <section className="relative w-full py-20 flex items-center overflow-hidden">
        {/* Fondos */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-black" />
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1604933762021-54a5858c9832?ixlib=rb-4.0.3')] bg-cover bg-center opacity-5" />
        
        {/* Efecto de resplandor */}
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-purple-800/20 rounded-full blur-[100px] animate-pulse" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-blue-800/20 rounded-full blur-[80px] animate-pulse delay-300" />
        </div>
        
        {/* Contenido */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 relative">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 bg-purple-900/20 backdrop-blur-sm rounded-full text-purple-400 mb-6 border border-purple-900/30">
              <Cookie className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">Documento Legal</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Política de{' '}
              <span className="relative">
                <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-300">
                  Cookies
                </span>
                <span className="absolute inset-0 bg-gradient-to-r from-purple-400/20 to-blue-300/20 blur-lg" />
              </span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Entérate de cómo usamos cookies para mejorar tu experiencia en nuestra plataforma.
            </p>
            
            <div className="flex flex-wrap justify-center gap-8 mb-12">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg backdrop-blur-sm border border-white/10">
                <Info className="w-5 h-5 text-purple-400" />
                <span className="text-gray-300">Actualizada: {new Date().toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contenido de la política */}
      <section className="py-16 relative overflow-hidden bg-gradient-to-br from-gray-950 via-gray-900/50 to-purple-950/30">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/3 w-[400px] h-[400px] bg-purple-900/15 rounded-full blur-[100px]" />
          <div className="absolute bottom-1/3 right-1/4 w-[300px] h-[300px] bg-blue-900/15 rounded-full blur-[80px]" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="bg-gradient-to-br from-gray-900/80 to-purple-950/50 p-8 md:p-12 rounded-xl backdrop-blur-sm border border-purple-500/20 shadow-lg">
            <div className="space-y-12">
              {/* Introducción */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">1. ¿Qué son las cookies?</h2>
                <p className="text-gray-300 mb-4">
                  Las cookies son pequeños archivos de texto que se almacenan en tu dispositivo (ordenador, tablet, smartphone) cuando visitas sitios web. Se utilizan ampliamente para hacer que los sitios web funcionen o trabajen de manera más eficiente, así como para proporcionar información a los propietarios del sitio.
                </p>
                <p className="text-gray-300">
                  Curvia AI utiliza cookies para diferentes propósitos, incluyendo mejorar tu experiencia en nuestro sitio web, recordar tus preferencias y proporcionarte contenido relevante.
                </p>
              </div>
              
              {/* Tipos de cookies */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">2. Tipos de cookies que utilizamos</h2>
                <p className="text-gray-300 mb-6">
                  Utilizamos los siguientes tipos de cookies en nuestro sitio web:
                </p>
                
                <div className="space-y-6">
                  <div className="bg-white/5 p-6 rounded-lg border border-purple-500/20">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 bg-green-900/50 rounded-full flex items-center justify-center text-green-400">
                        <Check className="w-5 h-5" />
                      </div>
                      <h3 className="text-xl font-bold text-white">Cookies esenciales</h3>
                    </div>
                    <p className="text-gray-300">
                      Estas cookies son necesarias para el funcionamiento básico de nuestro sitio web. Te permiten navegar por nuestro sitio y utilizar sus funciones, como acceder a áreas seguras. Sin estas cookies, no podríamos proporcionar los servicios que has solicitado.
                    </p>
                  </div>
                  
                  <div className="bg-white/5 p-6 rounded-lg border border-purple-500/20">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 bg-blue-900/50 rounded-full flex items-center justify-center text-blue-400">
                        <Cookie className="w-5 h-5" />
                      </div>
                      <h3 className="text-xl font-bold text-white">Cookies de preferencias</h3>
                    </div>
                    <p className="text-gray-300">
                      Estas cookies permiten que nuestro sitio web recuerde información que cambia la forma en que se comporta o se ve el sitio, como tu idioma preferido o la región en la que te encuentras. Esto te permite una experiencia más personalizada.
                    </p>
                  </div>
                  
                  <div className="bg-white/5 p-6 rounded-lg border border-purple-500/20">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-8 h-8 bg-purple-900/50 rounded-full flex items-center justify-center text-purple-400">
                        <Info className="w-5 h-5" />
                      </div>
                      <h3 className="text-xl font-bold text-white">Cookies analíticas</h3>
                    </div>
                    <p className="text-gray-300">
                      Estas cookies nos ayudan a entender cómo los visitantes interactúan con nuestro sitio web. Utilizamos esta información para mejorar nuestro sitio y proporcionar una mejor experiencia de usuario. Toda la información que estas cookies recopilan es anónima.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Cookies específicas */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">3. Cookies específicas que utilizamos</h2>
                <p className="text-gray-300 mb-6">
                  A continuación se detallan las cookies específicas que utilizamos:
                </p>
                
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-purple-900/30 border-b border-purple-500/30">
                        <th className="px-4 py-3 text-left text-white">Nombre</th>
                        <th className="px-4 py-3 text-left text-white">Propósito</th>
                        <th className="px-4 py-3 text-left text-white">Duración</th>
                        <th className="px-4 py-3 text-left text-white">Tipo</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-purple-500/20">
                      <tr className="bg-white/5">
                        <td className="px-4 py-3 text-gray-300">session_id</td>
                        <td className="px-4 py-3 text-gray-300">Mantener la sesión del usuario</td>
                        <td className="px-4 py-3 text-gray-300">Sesión</td>
                        <td className="px-4 py-3 text-gray-300">Esencial</td>
                      </tr>
                      <tr className="bg-white/5">
                        <td className="px-4 py-3 text-gray-300">auth_token</td>
                        <td className="px-4 py-3 text-gray-300">Autenticación de usuario</td>
                        <td className="px-4 py-3 text-gray-300">30 días</td>
                        <td className="px-4 py-3 text-gray-300">Esencial</td>
                      </tr>
                      <tr className="bg-white/5">
                        <td className="px-4 py-3 text-gray-300">language_preference</td>
                        <td className="px-4 py-3 text-gray-300">Recordar preferencia de idioma</td>
                        <td className="px-4 py-3 text-gray-300">1 año</td>
                        <td className="px-4 py-3 text-gray-300">Preferencia</td>
                      </tr>
                      <tr className="bg-white/5">
                        <td className="px-4 py-3 text-gray-300">_ga</td>
                        <td className="px-4 py-3 text-gray-300">Google Analytics - distinguir usuarios</td>
                        <td className="px-4 py-3 text-gray-300">2 años</td>
                        <td className="px-4 py-3 text-gray-300">Analítica</td>
                      </tr>
                      <tr className="bg-white/5">
                        <td className="px-4 py-3 text-gray-300">_gid</td>
                        <td className="px-4 py-3 text-gray-300">Google Analytics - distinguir usuarios</td>
                        <td className="px-4 py-3 text-gray-300">24 horas</td>
                        <td className="px-4 py-3 text-gray-300">Analítica</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Gestión de cookies */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">4. Cómo gestionar tus cookies</h2>
                <p className="text-gray-300 mb-6">
                  Puedes controlar y gestionar las cookies de varias formas. Ten en cuenta que eliminar o bloquear cookies puede afectar a tu experiencia en nuestro sitio web y es posible que algunas partes no funcionen correctamente.
                </p>
                
                <div className="space-y-6">
                  <div className="bg-white/5 p-6 rounded-lg border border-purple-500/20">
                    <h3 className="text-xl font-bold text-white mb-3">Configuración del navegador</h3>
                    <p className="text-gray-300 mb-4">
                      La mayoría de los navegadores web te permiten controlar la mayoría de las cookies a través de la configuración de tu navegador. Para saber más sobre cómo gestionar las cookies en tu navegador, visita los siguientes enlaces:
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                        <a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 transition-colors">
                          Google Chrome
                        </a>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                        <a href="https://support.mozilla.org/es/kb/habilitar-y-deshabilitar-cookies-sitios-web-rastrear-preferencias" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 transition-colors">
                          Mozilla Firefox
                        </a>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                        <a href="https://support.apple.com/es-es/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 transition-colors">
                          Safari
                        </a>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                        <a href="https://support.microsoft.com/es-es/microsoft-edge/eliminar-las-cookies-en-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 transition-colors">
                          Microsoft Edge
                        </a>
                      </li>
                    </ul>
                  </div>
                  
                  <div className="bg-white/5 p-6 rounded-lg border border-purple-500/20">
                    <h3 className="text-xl font-bold text-white mb-3">Google Analytics</h3>
                    <p className="text-gray-300">
                      Puedes optar por no ser rastreado por Google Analytics instalando el complemento de inhabilitación para navegadores de Google Analytics disponible en{' '}
                      <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 transition-colors">
                        https://tools.google.com/dlpage/gaoptout
                      </a>
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Aviso de cookies */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">5. Nuestro aviso de cookies</h2>
                <p className="text-gray-300 mb-4">
                  Cuando visitas nuestro sitio web por primera vez, verás un aviso de cookies que te informa sobre el uso de cookies en nuestro sitio. Este aviso te da la opción de aceptar todas las cookies, rechazar las cookies no esenciales o personalizar tus preferencias.
                </p>
                <p className="text-gray-300">
                  Puedes cambiar tus preferencias en cualquier momento visitando nuestra Configuración de Cookies en la parte inferior de cada página.
                </p>
              </div>
              
              {/* Cambios en la política */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">6. Cambios en nuestra política de cookies</h2>
                <p className="text-gray-300 mb-4">
                  Podemos actualizar nuestra política de cookies de vez en cuando. Te notificaremos cualquier cambio publicando la nueva política en esta página.
                </p>
                <p className="text-gray-300">
                  Te recomendamos revisar esta política periódicamente para estar informado de cualquier cambio.
                </p>
              </div>
              
              {/* Contacto */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">7. Contáctanos</h2>
                <p className="text-gray-300 mb-4">
                  Si tienes alguna pregunta sobre nuestra política de cookies, no dudes en contactarnos.
                </p>
                
                <div className="mt-6 bg-white/5 p-6 rounded-lg border border-purple-500/20">
                  <p className="text-white mb-2">Email: privacy@curviaai.com</p>
                  <p className="text-white mb-2">Teléfono: +34 91 123 45 67</p>
                  <p className="text-white">Dirección: Madrid, España</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CookiesPolicyPage; 